// State, effects and handlers for CallOffWizard.tsx, split out to keep
// CallOffWizard.tsx under the 500-line cap. No behaviour change from the
// original inline hooks — moved verbatim (submit/build-payload logic lives
// in useCallOffWizardSubmit.ts instead).
import { useState, useEffect, useCallback, useMemo } from 'react';
import { callOffAPI } from '../../../lib/api';
import type { CallOff, WizardDelivery } from '../../../lib/api';
import { toast } from 'sonner';
import type { WizardWarehouse } from './WarehouseDeliveryModal';
import { useCallOffConfig } from '../../../hooks/useCallOffConfig';
import { todayCommittedBy } from '../shared/committedByDate';
import {
  type Supplier,
  type SupplierStat,
  type WizardStep,
  computeFillPercent,
  computeTotalQty,
  groupLinesToDeliveries,
} from './callOffWizardHelpers';

interface UseCallOffWizardStateArgs {
  open: boolean;
  onClose: () => void;
  /** When set, wizard opens in edit mode with existing call-off data */
  editCallOff?: CallOff | null;
  /** When set, the supplier picker is pre-filled with this supplier and the
   *  wizard advances straight to Step 2 — used by Stock Planning's "New
   *  Call Off" button so the user doesn't pick the supplier twice. */
  initialSupplierId?: number | null;
  /** CR-6293: seed the Open PO Days window from the launching context (e.g. the
   *  Stock Planning grid's Open PO Days), so the wizard inherits it instead of
   *  resetting to the default. */
  initialOpenPoDays?: number | null;
  /** CR-8191: seed the "Committed by" date from the launching context (Stock
   *  Planning's SAL Committed End Date). Empty or absent means today. */
  initialCommittedByDate?: string | null;
}

/**
 * Owns all non-submit state for CallOffWizard: supplier/warehouse data
 * fetching, edit-mode population, delivery list management, and the
 * step-navigation handlers. See useCallOffWizardSubmit for the create/update
 * submission logic.
 */
export function useCallOffWizardState({
  open, onClose, editCallOff, initialSupplierId, initialOpenPoDays, initialCommittedByDate,
}: UseCallOffWizardStateArgs) {
  const isEditMode = !!editCallOff;
  const [step, setStep] = useState<WizardStep>(1);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [supplierStats, setSupplierStats] = useState<SupplierStat[]>([]);
  const [pickupDate, setPickupDate] = useState('');
  // CR-6293: open-PO availability window for this call-off. Defaults to the
  // stock-planning value (7); the user can change it in the warehouse step,
  // and the pickup date extends it (latest-wins). Persisted on the call-off.
  const [openPoDays, setOpenPoDays] = useState(7);
  // CR-8191: SAL commitments dated on or before this count as committed in the
  // warehouse step (the same cut-off as Stock Planning's SAL Committed End
  // Date). Not stored on the call-off. Never blank — empty means today.
  const [committedByDate, setCommittedByDateValue] = useState(todayCommittedBy);
  const setCommittedByDate = useCallback(
    (value: string) => setCommittedByDateValue(value || todayCommittedBy()),
    []
  );
  const [notes, setNotes] = useState('');
  const [deliveries, setDeliveries] = useState<WizardDelivery[]>([]);

  // Searchable supplier dropdown state
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);

  // Active warehouses for the current customer — fetched once on open and
  // shared with the WarehouseDeliveryModal so both sides agree on what's valid.
  const [warehouses, setWarehouses] = useState<WizardWarehouse[]>([]);
  const validWarehouseIds = useMemo(
    () => new Set(warehouses.map(w => w.id)),
    [warehouses]
  );

  /**
   * A delivery is stale if it's a warehouse delivery whose destinationId
   * doesn't match any active warehouse — usually because the warehouse was
   * soft-deleted or renamed since the call-off was created.
   */
  const isStaleDelivery = useCallback(
    (delivery: WizardDelivery): boolean => {
      if (delivery.type !== 'warehouse') return false;
      // Wait until warehouses have loaded before flagging anything
      if (warehouses.length === 0) return false;
      return delivery.destinationId == null || !validWarehouseIds.has(delivery.destinationId);
    },
    [warehouses.length, validWarehouseIds]
  );

  const hasStaleDeliveries = useMemo(
    () => deliveries.some(isStaleDelivery),
    [deliveries, isStaleDelivery]
  );

  const filteredSuppliers = useMemo(() => {
    const term = supplierSearch.toLowerCase();
    if (!term) return suppliers;
    return suppliers.filter(s => s.name.toLowerCase().includes(term));
  }, [suppliers, supplierSearch]);

  // Modal states
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [showDirectModal, setShowDirectModal] = useState(false);
  const [editingDeliveryIndex, setEditingDeliveryIndex] = useState<number | null>(null);

  // Per-customer config drives whether Direct Delivery is offered.
  const { config: callOffConfig } = useCallOffConfig();

  const fillPercent = useMemo(() => computeFillPercent(deliveries), [deliveries]);
  const totalQty = useMemo(() => computeTotalQty(deliveries), [deliveries]);

  // Fill % excluding the delivery currently being edited, so the modal
  // doesn't double-count it (existing + modal = correct total)
  const fillPercentExcludingEdited = useMemo(() => {
    if (editingDeliveryIndex === null) return fillPercent;
    const otherDeliveries = deliveries.filter((_, i) => i !== editingDeliveryIndex);
    return computeFillPercent(otherDeliveries);
  }, [deliveries, editingDeliveryIndex, fillPercent]);

  const generatedReference = useMemo(() => {
    if (isEditMode && editCallOff) return editCallOff.reference;
    if (!selectedSupplier) return '';
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yy = String(now.getFullYear()).slice(-2);
    // CR-7781: MMYY + the customer's 3-digit sequence, no supplier code —
    // mirrors CallOffCreationService._generate_reference.
    return `${mm}${yy}XXX`;
  }, [selectedSupplier, isEditMode, editCallOff]);

  const fetchSuppliers = useCallback(async () => {
    setLoadingSuppliers(true);
    try {
      const res = await callOffAPI.getSuppliers();
      setSuppliers(res.data || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load suppliers';
      toast.error(message);
    } finally {
      setLoadingSuppliers(false);
    }
  }, []);

  const fetchSupplierStats = useCallback(async () => {
    try {
      const res = await callOffAPI.getSupplierStats();
      setSupplierStats(res.data || []);
    } catch {
      // Non-critical, ignore
    }
  }, []);

  const fetchWarehouses = useCallback(async () => {
    try {
      const res = await callOffAPI.getWarehouses();
      setWarehouses(res.data || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load warehouses';
      toast.error(message);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchSuppliers();
      fetchSupplierStats();
      fetchWarehouses();
    }
  }, [open, fetchSuppliers, fetchSupplierStats, fetchWarehouses]);

  // Populate wizard in edit mode
  useEffect(() => {
    if (!open || !editCallOff || suppliers.length === 0) return;

    const supplier = suppliers.find(s => s.id === editCallOff.supplier);
    if (supplier) {
      setSelectedSupplier(supplier);
    }

    setPickupDate(editCallOff.pickup_date || '');
    setOpenPoDays(editCallOff.open_po_days ?? 7);
    setNotes(editCallOff.notes || '');

    // Group lines by delivery destination into wizard deliveries
    if (editCallOff.lines && editCallOff.lines.length > 0) {
      const grouped = groupLinesToDeliveries(editCallOff.lines, editCallOff.line_ids_on_asn);
      setDeliveries(grouped);
    }

    // Skip to step 2 in edit mode
    setStep(2);
  }, [open, editCallOff, suppliers]);

  // Pre-select the supplier when the caller seeded one (e.g. Stock Planning's
  // "New Call Off" passing through the page-level supplier filter). Skipped
  // in edit mode because that flow has its own pre-fill above.
  useEffect(() => {
    if (!open || editCallOff || !initialSupplierId || suppliers.length === 0) return;
    if (selectedSupplier && selectedSupplier.id === initialSupplierId) return;

    const supplier = suppliers.find(s => s.id === initialSupplierId);
    if (supplier) {
      setSelectedSupplier(supplier);
      setStep(2);
    }
  }, [open, editCallOff, initialSupplierId, suppliers, selectedSupplier]);

  // CR-6293: inherit the Open PO Days the user set in Stock Planning when they
  // launch "New Call Off" from there (create mode only — edit mode pre-fills
  // from the saved call-off above). Depends only on open/initialOpenPoDays so it
  // won't clobber a value the user then changes inside the wizard.
  useEffect(() => {
    if (!open || editCallOff) return;
    setOpenPoDays(initialOpenPoDays ?? 7);
  }, [open, editCallOff, initialOpenPoDays]);

  // CR-8191: the "Committed by" date starts from Stock Planning's SAL Committed
  // End Date when launched from there, otherwise today — in edit mode too, as
  // the date is not stored on the call-off. Runs on open only, so it won't
  // clobber a date the user then changes inside the wizard.
  useEffect(() => {
    if (!open) return;
    setCommittedByDate(initialCommittedByDate ?? '');
  }, [open, initialCommittedByDate, setCommittedByDate]);

  const resetWizard = useCallback(() => {
    setStep(1);
    setSelectedSupplier(null);
    setSupplierSearch('');
    setSupplierDropdownOpen(false);
    setPickupDate('');
    setNotes('');
    setDeliveries([]);
    setEditingDeliveryIndex(null);
  }, []);

  const handleClose = () => {
    if (deliveries.length > 0) {
      if (!window.confirm('You have unsaved deliveries. Are you sure you want to close?')) {
        return;
      }
    }
    resetWizard();
    onClose();
  };

  const handleSupplierChange = (supplierId: string) => {
    const supplier = suppliers.find(s => String(s.id) === supplierId);
    setSelectedSupplier(supplier || null);
  };

  const selectedSupplierStat = useMemo(() => {
    if (!selectedSupplier) return null;
    return supplierStats.find(s => s['supplier__id'] === selectedSupplier.id) || null;
  }, [selectedSupplier, supplierStats]);

  const handleAddWarehouseDelivery = (delivery: WizardDelivery) => {
    if (editingDeliveryIndex !== null) {
      setDeliveries(prev => {
        const updated = [...prev];
        updated[editingDeliveryIndex] = delivery;
        return updated;
      });
      setEditingDeliveryIndex(null);
    } else {
      setDeliveries(prev => [...prev, delivery]);
    }
    setShowWarehouseModal(false);
    setStep(2);
  };

  const handleAddDirectDeliveries = (newDeliveries: WizardDelivery[]) => {
    if (editingDeliveryIndex !== null) {
      // Replace the edited delivery
      setDeliveries(prev => {
        const updated = [...prev];
        updated.splice(editingDeliveryIndex, 1, ...newDeliveries);
        return updated;
      });
      setEditingDeliveryIndex(null);
    } else {
      setDeliveries(prev => [...prev, ...newDeliveries]);
    }
    setShowDirectModal(false);
    setStep(2);
  };

  const handleRemoveDelivery = (index: number) => {
    setDeliveries(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditDelivery = (index: number) => {
    const delivery = deliveries[index];
    setEditingDeliveryIndex(index);
    if (delivery.type === 'warehouse') {
      setShowWarehouseModal(true);
    } else {
      setShowDirectModal(true);
    }
  };

  const handleBackFromStep2 = () => {
    if (deliveries.length > 0) {
      if (!window.confirm('Going back will clear your deliveries. Continue?')) {
        return;
      }
      setDeliveries([]);
    }
    setStep(1);
  };

  const editDelivery = editingDeliveryIndex !== null ? deliveries[editingDeliveryIndex] : undefined;

  return {
    isEditMode,
    step, setStep,
    suppliers, loadingSuppliers,
    selectedSupplier, setSelectedSupplier,
    pickupDate, setPickupDate,
    openPoDays, setOpenPoDays,
    committedByDate, setCommittedByDate,
    notes, setNotes,
    deliveries, setDeliveries,
    supplierSearch, setSupplierSearch,
    supplierDropdownOpen, setSupplierDropdownOpen,
    warehouses,
    isStaleDelivery, hasStaleDeliveries,
    filteredSuppliers,
    showWarehouseModal, setShowWarehouseModal,
    showDirectModal, setShowDirectModal,
    editingDeliveryIndex, setEditingDeliveryIndex,
    callOffConfig,
    fillPercent, totalQty, fillPercentExcludingEdited,
    generatedReference,
    resetWizard,
    handleClose,
    handleSupplierChange,
    selectedSupplierStat,
    handleAddWarehouseDelivery,
    handleAddDirectDeliveries,
    handleRemoveDelivery,
    handleEditDelivery,
    handleBackFromStep2,
    editDelivery,
  };
}
